import { type FormEvent, useEffect, useMemo, useState } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { ArrowLeft, Plus, RefreshCw, Save, Search, Server } from "lucide-react"
import {
  smartoltService,
  type SmartOLTAlarmRow,
  type SmartOLTOltMgmtIpRow,
  type SmartOLTOltMgmtIps,
  type SmartOLTOltRow,
  type SmartOLTOnuRow,
} from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import { dateTime } from "../utils.ts"
import { displayName, formatSignal, statusKind } from "./smartoltConfiguredUtils.ts"
import {
  billingForOlt,
  dash,
  extractPresetList,
  pick,
  presetSettings,
  speedLabel,
  yesNo,
} from "./smartoltOltDetailUtils.ts"
import { SmartOLTMgmtIpModal } from "./SmartOLTMgmtIpModal.tsx"

type Tab =
  | "overview"
  | "cards"
  | "pon_ports"
  | "uplinks"
  | "vlans"
  | "mgmt"
  | "profiles"
  | "cli"
  | "vpn"
  | "graphs"
  | "voip"
  | "alarms"
  | "templates"

const TABS: { id: Tab; label: string }[] = [
  { id: "overview", label: "OLT Details" },
  { id: "cards", label: "OLT Cards" },
  { id: "pon_ports", label: "PON Ports" },
  { id: "uplinks", label: "Uplink" },
  { id: "vlans", label: "VLANs" },
  { id: "mgmt", label: "ONU Mgmt IPs" },
  { id: "profiles", label: "Olt Profiles" },
  { id: "cli", label: "Cli Users" },
  { id: "vpn", label: "VPN" },
  { id: "graphs", label: "Graphs" },
  { id: "voip", label: "VoIP Profiles" },
  { id: "alarms", label: "Alarms" },
  { id: "templates", label: "Provisioning Templates" },
]

const LIVE_SECTIONS_BY_TAB: Partial<Record<Tab, string | string[]>> = {
  overview: "health",
  cards: "cards",
  pon_ports: "pon_ports",
  uplinks: "uplinks",
  vlans: "vlans",
  profiles: ["config_presets", "speed_profiles"],
  templates: "presets",
  vpn: "billing",
}

function statusTone(value: unknown) {
  const text = String(value || "").toLowerCase()
  if (["up", "online", "enabled", "normal"].some((token) => text.includes(token))) return "text-emerald-600 font-semibold"
  if (["down", "disabled", "outage", "offline"].some((token) => text.includes(token))) return "text-rose-600 font-semibold"
  return "text-slate-500 font-medium"
}

function RefreshButton({ busy, onClick, label }: { busy: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 shadow-xs transition-colors hover:bg-slate-50 disabled:opacity-40"
    >
      <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin text-blue-600" : ""}`} />
      {busy ? "Refreshing..." : label}
    </button>
  )
}

export function SmartOLTOltDetailPage() {
  const { oltId = "" } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [olt, setOlt] = useState<SmartOLTOltRow | null>(null)
  const [detail, setDetail] = useState<any>(null)
  const [onus, setOnus] = useState<SmartOLTOnuRow[]>([])
  const [loading, setLoading] = useState(true)
  const hashTab = location.hash.replace("#", "") as Tab
  const [tab, setTab] = useState<Tab>(hashTab || "overview")
  const [savingConfig, setSavingConfig] = useState(false)
  const [showAddVlan, setShowAddVlan] = useState(false)
  const [savingVlan, setSavingVlan] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [configPresets, setConfigPresets] = useState<any[]>([])
  const [authPresets, setAuthPresets] = useState<any[]>([])
  const [billing, setBilling] = useState<any>(null)
  const [alarms, setAlarms] = useState<SmartOLTAlarmRow[]>([])
  const [graphOnuId, setGraphOnuId] = useState("")
  const [graphKind, setGraphKind] = useState<"traffic" | "signal">("traffic")
  const [graphUrl, setGraphUrl] = useState<string | null>(null)
  const [graphLoading, setGraphLoading] = useState(false)
  const [loadedLive, setLoadedLive] = useState<Partial<Record<Tab, boolean>>>({})
  const [mgmtIps, setMgmtIps] = useState<SmartOLTOltMgmtIps | null>(null)
  const [mgmtSearch, setMgmtSearch] = useState("")
  const [mgmtModalOnu, setMgmtModalOnu] = useState<SmartOLTOltMgmtIpRow | null>(null)
  const [mgmtPickId, setMgmtPickId] = useState("")
  const [presetOnuId, setPresetOnuId] = useState("")
  const [presetKey, setPresetKey] = useState("")
  const [presetBusy, setPresetBusy] = useState(false)
  const [sectionError, setSectionError] = useState<string | null>(null)
  const [vlanForm, setVlanForm] = useState({
    vlan: "",
    description: "",
    for_iptv: "0",
    for_mgmt_voip: "0",
    dhcp_snooping: "0",
    lan_to_lan: "0",
  })

  useEffect(() => {
    const hash = location.hash.replace("#", "") as Tab
    if (hash) setTab(hash)
  }, [location.hash])

  useEffect(() => {
    if (!oltId) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const [oltRows, oltDetail, onuRows, alarmData] = await Promise.all([
          smartoltService.getOlts(),
          smartoltService.getOltDetail(oltId),
          smartoltService.getOnus({ limit: 5000 }),
          smartoltService.getAlarms({ state: "open", range: "7d" }).catch(() => null),
        ])
        if (cancelled) return
        const match = (Array.isArray(oltRows) ? oltRows : []).find((row) => row.olt_id === oltId) || null
        setOlt(match)
        setDetail(oltDetail || null)
        setMgmtIps(oltDetail?.mgmt_ips || null)
        setOnus((Array.isArray(onuRows) ? onuRows : []).filter((row) => String(row.olt_id) === oltId))
        setAlarms((alarmData?.alarms || []).filter((row) => String(row.olt_id || "") === oltId))
      } catch {
        toast.error("Failed to load OLT details")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [oltId])

  const ponPorts = useMemo(() => (Array.isArray(detail?.pon_ports) ? detail.pon_ports : []), [detail])
  const cards = useMemo(() => (Array.isArray(detail?.cards) ? detail.cards : []), [detail])
  const uplinks = useMemo(() => (Array.isArray(detail?.uplink_ports) ? detail.uplink_ports : []), [detail])
  const vlans = useMemo(() => (Array.isArray(detail?.vlans) ? detail.vlans : []), [detail])
  const speedProfiles = useMemo(() => (Array.isArray(detail?.speed_profiles) ? detail.speed_profiles : []), [detail])
  const online = onus.filter((row) => statusKind(row) === "online").length
  const oltInfo = detail?.olt || {}
  const name = olt?.name || oltInfo.name || oltId
  const mgmtAssigned = useMemo(() => {
    const rows = mgmtIps?.assigned || []
    const q = mgmtSearch.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((row) =>
      [row.name, row.onu_external_id, row.ip_address, row.default_gateway].filter(Boolean).join(" ").toLowerCase().includes(q),
    )
  }, [mgmtIps, mgmtSearch])
  const mgmtPools = mgmtIps?.pools || []
  const voipOnus = onus.filter((row) => row.voip && String(row.voip) !== "0" && String(row.voip).toLowerCase() !== "disabled")
  const outagePons = useMemo(() => (Array.isArray(detail?.outage_pons) ? detail.outage_pons : []), [detail])

  const setHashTab = (next: Tab) => {
    setTab(next)
    navigate({ hash: next === "overview" ? "" : next }, { replace: true })
  }

  const applyRefreshPayload = (section: string, payload: any) => {
    if (payload?.detail) {
      setDetail(payload.detail)
      if (payload.detail.mgmt_ips) setMgmtIps(payload.detail.mgmt_ips)
    }
    const items = Array.isArray(payload?.items) ? payload.items : extractPresetList(payload?.live ?? payload?.items)
    if (section === "presets") setAuthPresets(items)
    if (section === "config_presets") setConfigPresets(items)
    if (section === "billing") setBilling(billingForOlt(items, oltId))
    if (section === "speed_profiles" && payload?.detail?.speed_profiles) {
      setDetail(payload.detail)
    }
  }

  const refreshSection = async (section: string, silent = false): Promise<boolean> => {
    if (!oltId || typeof smartoltService.refreshOltSection !== "function") return false
    if (!silent) setRefreshing(true)
    try {
      const payload = await smartoltService.refreshOltSection(oltId, section)
      if (!payload?.ok) {
        const message = payload?.error || "Failed to refresh from SmartOLT"
        if (!silent) toast.error(message)
        return false
      }
      applyRefreshPayload(section, payload)
      if (!silent) toast.success("Section refreshed from SmartOLT")
      return true
    } catch (error: any) {
      if (!silent) toast.error(error?.response?.data?.error || "Failed to refresh from SmartOLT")
      return false
    } finally {
      if (!silent) setRefreshing(false)
    }
  }

  useEffect(() => {
    const spec = LIVE_SECTIONS_BY_TAB[tab]
    if (!spec || !oltId || loading || loadedLive[tab]) return
    const sections = Array.isArray(spec) ? spec : [spec]
    let cancelled = false
    setSectionError(null)
    Promise.all(sections.map((section) => refreshSection(section, true))).then((results) => {
      if (cancelled) return
      if (results.some((ok) => !ok)) {
        setSectionError("Some sections could not be refreshed from SmartOLT. Check tenant config and API budget.")
      }
      setLoadedLive((current) => ({ ...current, [tab]: true }))
    })
    return () => {
      cancelled = true
    }
  }, [tab, oltId, loading])

  useEffect(() => {
    if (tab !== "mgmt" || !oltId || loading) return
    let cancelled = false
    smartoltService.getOltMgmtIps(oltId).then((payload) => {
      if (!cancelled) setMgmtIps(payload)
    }).catch(() => {
      if (!cancelled) setSectionError("Failed to load management IP assignments.")
    })
    return () => {
      cancelled = true
    }
  }, [tab, oltId, loading, detail?.mgmt_ips?.summary?.assigned])

  useEffect(() => {
    let objectUrl = graphUrl
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [graphUrl])

  const saveOltConfig = async () => {
    const ok = window.confirm("Save all OLTs config in SmartOLT now?")
    if (!ok) return
    setSavingConfig(true)
    try {
      await smartoltService.createCommand("save_olts_config", {})
      await smartoltService.runCommandsNow(5)
      toast.success("Save OLTs config command sent")
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to save OLTs config")
    } finally {
      setSavingConfig(false)
    }
  }

  const updateVlanForm = (field: keyof typeof vlanForm, value: string) => {
    setVlanForm((current) => ({ ...current, [field]: value }))
  }

  const addVlan = async (event: FormEvent) => {
    event.preventDefault()
    if (!vlanForm.vlan.trim()) {
      toast.error("VLAN ID is required")
      return
    }
    const form_data = Object.fromEntries(
      Object.entries(vlanForm).map(([key, value]) => [key, value.trim()]).filter(([, value]) => value),
    )
    setSavingVlan(true)
    try {
      await smartoltService.createCommand("add_vlan", { path_params: { olt_id: oltId }, form_data })
      await smartoltService.runCommandsNow(5)
      toast.success("VLAN add command sent")
      setVlanForm({ vlan: "", description: "", for_iptv: "0", for_mgmt_voip: "0", dhcp_snooping: "0", lan_to_lan: "0" })
      setShowAddVlan(false)
      await refreshSection("vlans", true)
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to add VLAN")
    } finally {
      setSavingVlan(false)
    }
  }

  const reloadMgmtIps = async () => {
    if (!oltId) return
    try {
      const payload = await smartoltService.getOltMgmtIps(oltId)
      setMgmtIps(payload)
    } catch {
      /* keep cached */
    }
  }

  const openMgmtModal = (row: SmartOLTOltMgmtIpRow | SmartOLTOnuRow) => {
    const name = "name" in row && row.name ? String(row.name) : displayName(row)
    setMgmtModalOnu({
      onu_external_id: row.onu_external_id,
      name,
      ip_address: "ip_address" in row ? row.ip_address : row.mgmt_ip,
      subnet_mask: "subnet_mask" in row ? row.subnet_mask : row.mgmt_subnet,
      default_gateway: "default_gateway" in row ? row.default_gateway : row.mgmt_gateway,
      dns1: "dns1" in row ? row.dns1 : row.mgmt_dns1,
      dns2: "dns2" in row ? row.dns2 : row.mgmt_dns2,
      mgmt_vlan: row.mgmt_vlan != null ? String(row.mgmt_vlan) : null,
    })
  }

  const applyConfigPreset = async () => {
    if (!presetOnuId || !presetKey) {
      toast.error("Select an ONU and configuration preset")
      return
    }
    const preset = configPresets.find((row) => String(row.id || row.name) === presetKey)
    if (!preset) {
      toast.error("Invalid preset")
      return
    }
    setPresetBusy(true)
    try {
      const form_data: Record<string, string> = preset.id
        ? { configuration_preset_id: String(preset.id) }
        : { configuration_preset_name: String(preset.name) }
      await smartoltService.applyConfigurationPreset(presetOnuId, form_data)
      await smartoltService.runCommandsNow(5)
      toast.success("Configuration preset apply command sent")
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to apply preset")
    } finally {
      setPresetBusy(false)
    }
  }

  const removeConfigPreset = async () => {
    if (!presetOnuId) {
      toast.error("Select an ONU")
      return
    }
    const ok = window.confirm("Remove configuration preset from this ONU?")
    if (!ok) return
    setPresetBusy(true)
    try {
      await smartoltService.removeConfigurationPreset(presetOnuId)
      await smartoltService.runCommandsNow(5)
      toast.success("Configuration preset remove command sent")
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to remove preset")
    } finally {
      setPresetBusy(false)
    }
  }

  const loadGraph = async (onuExternalId: string, kind: "traffic" | "signal") => {
    if (!onuExternalId || typeof smartoltService.getXrtgGraphImage !== "function") return
    setGraphLoading(true)
    try {
      const blob = await smartoltService.getXrtgGraphImage(onuExternalId, { kind, range: "1hr" })
      setGraphUrl((current) => {
        if (current) URL.revokeObjectURL(current)
        return URL.createObjectURL(blob)
      })
    } catch {
      setGraphUrl(null)
      toast.error("Failed to load OLT graph")
    } finally {
      setGraphLoading(false)
    }
  }

  return (
    <div className="space-y-4 sm:space-y-6">
        {/* Header banner */}
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200/80 bg-white p-4 shadow-xs">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(SMARTOLT_PATHS.dashboard)}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Dashboard
            </button>
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 border border-blue-200 shadow-xs">
                <Server className="h-4.5 w-4.5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900">{name}</h1>
                <p className="text-xs text-slate-500">OLT Telemetry & Configuration</p>
              </div>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 shadow-xs">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            {online}/{onus.length} ONUs online
          </span>
        </div>

        {/* Metric cards */}
        <div className="grid gap-2 sm:grid-cols-5">
          {[
            ["IP", olt?.ip || oltInfo.ip],
            ["Sys-Name", olt?.hardware_version || oltInfo.hardware_version],
            ["Uptime", detail?.health?.uptime],
            ["Temperature", detail?.health?.env_temp],
            ["Last read", dateTime(detail?.health?.fetched_at)],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-slate-200/80 bg-white p-3 shadow-xs">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
              <p className="mt-1 text-sm font-bold text-slate-900 truncate">{dash(value)}</p>
            </div>
          ))}
        </div>

        {/* Tabs and Content */}
        <section className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-xs">
          <div className="flex flex-wrap gap-1 border-b border-slate-200 bg-slate-50/70 p-2 overflow-x-auto">
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setHashTab(item.id)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                  tab === item.id
                    ? "bg-blue-600 text-white shadow-xs"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="p-4">
            {sectionError ? <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs font-medium text-amber-800 shadow-xs">{sectionError}</p> : null}
            {loading && <p className="text-sm text-slate-500">Loading OLT details…</p>}

            {tab === "overview" && !loading && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Link to={SMARTOLT_PATHS.settingsOlts} className="inline-flex h-8 items-center rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">
                    Edit OLT Settings
                  </Link>
                  <Link to={`${SMARTOLT_PATHS.diagnostics}?q=${encodeURIComponent(name)}`} className="inline-flex h-8 items-center rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">
                    See History
                  </Link>
                  <button type="button" onClick={() => setHashTab("cli")} className="inline-flex h-8 items-center rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">
                    CLI
                  </button>
                  <button
                    type="button"
                    disabled={savingConfig}
                    onClick={saveOltConfig}
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50"
                  >
                    <Save className="h-3.5 w-3.5" />
                    {savingConfig ? "Saving..." : "Save Config"}
                  </button>
                  <RefreshButton busy={refreshing} onClick={() => refreshSection("health")} label="Refresh Details" />
                </div>
                <div className="grid gap-2.5 sm:grid-cols-3 text-sm">
                  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 shadow-xs">
                    <p className="text-xs font-medium text-slate-500">Hardware</p>
                    <p className="mt-1 font-bold text-slate-900">{dash(olt?.hardware_version || oltInfo.hardware_version)}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 shadow-xs">
                    <p className="text-xs font-medium text-slate-500">Telnet / SNMP</p>
                    <p className="mt-1 font-bold text-slate-900">{dash(olt?.telnet_port || oltInfo.telnet_port)} / {dash(olt?.snmp_port || oltInfo.snmp_port)}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 shadow-xs">
                    <p className="text-xs font-medium text-slate-500">PON ports</p>
                    <p className="mt-1 font-bold text-slate-900">{ponPorts.length}</p>
                  </div>
                  <Link
                    to={`${SMARTOLT_PATHS.configured}?olt=${encodeURIComponent(oltId)}`}
                    className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 shadow-xs hover:bg-blue-50/50 transition-colors"
                  >
                    <p className="text-xs font-medium text-slate-500">Configured ONUs</p>
                    <p className="mt-1 font-bold text-blue-600 hover:underline">{onus.length}</p>
                  </Link>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 shadow-xs">
                    <p className="text-xs font-medium text-slate-500">VLANs</p>
                    <p className="mt-1 font-bold text-slate-900">{vlans.length}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 shadow-xs">
                    <p className="text-xs font-medium text-slate-500">Uplinks</p>
                    <p className="mt-1 font-bold text-slate-900">{uplinks.length}</p>
                  </div>
                </div>
              </div>
            )}

            {tab === "cards" && (
              <div className="space-y-3">
                <RefreshButton busy={refreshing} onClick={() => refreshSection("cards")} label="Refresh OLT Cards" />
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Slot</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Real type</th>
                        <th className="px-3 py-2 text-left">Ports</th>
                        <th className="px-3 py-2 text-left">Software</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Role</th>
                        <th className="px-3 py-2 text-left">Updated</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {cards.length === 0 ? (
                        <tr><td colSpan={8} className="py-6 text-center text-slate-500">No cards cached. Refresh to read live SmartOLT data.</td></tr>
                      ) : (
                        cards.map((card: any, index: number) => (
                          <tr key={`${card.slot}-${index}`} className="transition-colors hover:bg-slate-50/80 text-slate-700">
                            <td className="px-3 py-2 font-mono font-medium">{dash(card.slot)}</td>
                            <td className="px-3 py-2">{dash(card.card_type)}</td>
                            <td className="px-3 py-2">{dash(card.real_type)}</td>
                            <td className="px-3 py-2 font-mono">{dash(card.ports)}</td>
                            <td className="px-3 py-2">{dash(card.software_version)}</td>
                            <td className={`px-3 py-2 ${statusTone(card.status)}`}>{dash(card.status)}</td>
                            <td className="px-3 py-2 text-slate-600">{dash(card.role)}</td>
                            <td className="px-3 py-2 text-slate-500">{dash(card.info_updated)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === "pon_ports" && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <RefreshButton busy={refreshing} onClick={() => refreshSection("pon_ports")} label="Refresh PON Ports" />
                  {outagePons.length > 0 ? (
                    <span className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 shadow-xs">{outagePons.length} PON ports in outage</span>
                  ) : null}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Board</th>
                        <th className="px-3 py-2 text-left">Port</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Admin</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">ONUs</th>
                        <th className="px-3 py-2 text-left">Avg signal</th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-left">Range</th>
                        <th className="px-3 py-2 text-left">TX power</th>
                        <th className="px-3 py-2 text-left">Default VLAN</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {ponPorts.length === 0 ? (
                        <tr><td colSpan={11} className="py-6 text-center text-slate-500">No PON ports cached. Refresh to read live SmartOLT data.</td></tr>
                      ) : (
                        ponPorts.map((port: any) => (
                          <tr key={`${port.board}-${port.pon_port}`} className="transition-colors hover:bg-slate-50/80 text-slate-700">
                            <td className="px-3 py-2 font-mono font-medium">{dash(port.board)}</td>
                            <td className="px-3 py-2">
                              <Link
                                to={`${SMARTOLT_PATHS.configured}?olt=${encodeURIComponent(oltId)}&board=${encodeURIComponent(port.board || "")}&port=${encodeURIComponent(port.pon_port || "")}`}
                                className="font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {dash(port.pon_port)}
                              </Link>
                            </td>
                            <td className="px-3 py-2">{dash(port.pon_type)}</td>
                            <td className="px-3 py-2">{dash(port.admin_status)}</td>
                            <td className={`px-3 py-2 ${statusTone(port.operational_status)}`}>{dash(port.operational_status)}</td>
                            <td className="px-3 py-2">
                              <Link
                                to={`${SMARTOLT_PATHS.configured}?olt=${encodeURIComponent(oltId)}&board=${encodeURIComponent(port.board || "")}&port=${encodeURIComponent(port.pon_port || "")}&status=online`}
                                className="text-emerald-600 hover:text-emerald-800 font-semibold"
                              >
                                Online: {dash(port.online_onus_count)}
                              </Link>
                              {" / "}
                              <Link
                                to={`${SMARTOLT_PATHS.configured}?olt=${encodeURIComponent(oltId)}&board=${encodeURIComponent(port.board || "")}&port=${encodeURIComponent(port.pon_port || "")}`}
                                className="text-blue-600 hover:text-blue-800 font-semibold"
                              >
                                Total: {dash(port.onus_count)}
                              </Link>
                            </td>
                            <td className="px-3 py-2">{formatSignal(port.average_signal)}</td>
                            <td className="px-3 py-2 text-slate-600">{dash(port.description)}</td>
                            <td className="px-3 py-2 text-slate-600">{dash(port.range)}</td>
                            <td className="px-3 py-2 font-mono text-slate-600">{dash(port.tx_power)}</td>
                            <td className="px-3 py-2 font-mono text-slate-600">{dash(port.default_vlan)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === "uplinks" && (
              <div className="space-y-3">
                <RefreshButton busy={refreshing} onClick={() => refreshSection("uplinks")} label="Refresh Uplink Ports" />
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Uplink port</th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Admin</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Negotiation</th>
                        <th className="px-3 py-2 text-left">MTU</th>
                        <th className="px-3 py-2 text-left">Wave</th>
                        <th className="px-3 py-2 text-left">Temp</th>
                        <th className="px-3 py-2 text-left">PVID</th>
                        <th className="px-3 py-2 text-left">Mode / tagged VLANs</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {uplinks.length === 0 ? (
                        <tr><td colSpan={11} className="py-6 text-center text-slate-500">No uplinks cached. Refresh to read live SmartOLT data.</td></tr>
                      ) : (
                        uplinks.map((uplink: any, index: number) => (
                          <tr key={`${uplink.name}-${index}`} className="transition-colors hover:bg-slate-50/80 text-slate-700">
                            <td className="px-3 py-2 font-mono font-semibold text-slate-900">{dash(uplink.name)}</td>
                            <td className="px-3 py-2 text-slate-600">{dash(uplink.description)}</td>
                            <td className="px-3 py-2">{dash(uplink.uplink_type)}</td>
                            <td className="px-3 py-2">{dash(uplink.admin_status)}</td>
                            <td className={`px-3 py-2 ${statusTone(uplink.status)}`}>{dash(uplink.status)}</td>
                            <td className="px-3 py-2 font-mono text-slate-600">{dash(uplink.negotiation)}</td>
                            <td className="px-3 py-2 font-mono text-slate-600">{dash(uplink.mtu)}</td>
                            <td className="px-3 py-2 font-mono text-slate-600">{dash(uplink.wavelength)}</td>
                            <td className="px-3 py-2 font-mono text-slate-600">{dash(uplink.temperature)}</td>
                            <td className="px-3 py-2 font-mono text-slate-600">{dash(uplink.pvid)}</td>
                            <td className="px-3 py-2">{dash(uplink.mode)}{uplink.vlan_tag ? `: ${uplink.vlan_tag}` : ""}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === "vlans" && (
              <div className="space-y-3">
                <div className="flex flex-wrap justify-end gap-2">
                  <RefreshButton busy={refreshing} onClick={() => refreshSection("vlans")} label="Refresh VLANs" />
                  <button type="button" onClick={() => setShowAddVlan((value) => !value)} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">
                    <Plus className="h-3.5 w-3.5" />
                    Add VLAN
                  </button>
                </div>
                <p className="text-xs text-slate-500">VLANs added here are not applied to uplink ports automatically. Tag them on the Uplink tab in SmartOLT if needed.</p>
                {showAddVlan ? (
                  <form onSubmit={addVlan} className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 shadow-xs">
                    <div className="grid gap-3 sm:grid-cols-3">
                      <label className="text-xs font-semibold text-slate-700">
                        VLAN ID
                        <input value={vlanForm.vlan} onChange={(event) => updateVlanForm("vlan", event.target.value)} className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none" />
                      </label>
                      <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
                        Description
                        <input value={vlanForm.description} onChange={(event) => updateVlanForm("description", event.target.value)} className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none" />
                      </label>
                      {[
                        ["for_iptv", "For IPTV"],
                        ["for_mgmt_voip", "For Mgmt/VoIP"],
                        ["dhcp_snooping", "DHCP snooping"],
                        ["lan_to_lan", "LAN to LAN"],
                      ].map(([field, label]) => (
                        <label key={field} className="flex items-center gap-2 text-xs font-medium text-slate-700">
                          <input
                            type="checkbox"
                            checked={vlanForm[field as keyof typeof vlanForm] === "1"}
                            onChange={(event) => updateVlanForm(field as keyof typeof vlanForm, event.target.checked ? "1" : "0")}
                            className="rounded border-slate-300 bg-white accent-blue-600"
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button type="submit" disabled={savingVlan} className="inline-flex h-9 items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow-xs hover:bg-blue-700 disabled:opacity-50">
                        {savingVlan ? "Adding..." : "Add VLAN"}
                      </button>
                    </div>
                  </form>
                ) : null}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">VLAN-ID</th>
                        <th className="px-3 py-2 text-left">Default for</th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-left">IPTV</th>
                        <th className="px-3 py-2 text-left">Mgmt/VoIP</th>
                        <th className="px-3 py-2 text-left">DHCP snooping</th>
                        <th className="px-3 py-2 text-left">LAN-to-LAN</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {vlans.length === 0 ? (
                        <tr><td colSpan={7} className="py-6 text-center text-slate-500">No VLANs cached for this OLT. Refresh to read live SmartOLT data.</td></tr>
                      ) : (
                        vlans.map((vlan: any) => (
                          <tr key={`${vlan.vlan_id}-${vlan.id || ""}`} className="transition-colors hover:bg-slate-50/80 text-slate-700">
                            <td className="px-3 py-2 font-mono">
                              <Link to={`${SMARTOLT_PATHS.configured}?olt=${encodeURIComponent(oltId)}&vlan=${encodeURIComponent(vlan.vlan_id)}`} className="font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                                {vlan.vlan_id}
                              </Link>
                            </td>
                            <td className="px-3 py-2">{dash(vlan.default_for)}</td>
                            <td className="px-3 py-2 text-slate-600">{dash(vlan.description)}</td>
                            <td className={`px-3 py-2 ${vlan.for_iptv ? "text-emerald-400" : "text-rose-400"}`}>{yesNo(vlan.for_iptv)}</td>
                            <td className={`px-3 py-2 ${vlan.for_mgmt_voip ? "text-emerald-400" : "text-rose-400"}`}>{yesNo(vlan.for_mgmt_voip)}</td>
                            <td className={`px-3 py-2 ${vlan.dhcp_snooping ? "text-emerald-400" : "text-rose-400"}`}>{yesNo(vlan.dhcp_snooping)}</td>
                            <td className={`px-3 py-2 ${vlan.lan_to_lan ? "text-emerald-400" : "text-rose-400"}`}>{yesNo(vlan.lan_to_lan)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === "mgmt" && (
              <div className="space-y-4">
                <p className="text-xs text-slate-500">
                  IP pool configuration is managed in Xelynx/SmartOLT web UI. Assigned ONU IPs below come from synced ONU details.
                </p>
                <div>
                  <h2 className="mb-2 text-sm font-bold text-slate-900">IP pools</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                        <tr>
                          <th className="px-3 py-2 text-left">IP address</th>
                          <th className="px-3 py-2 text-left">Subnet mask</th>
                          <th className="px-3 py-2 text-left">Default gateway</th>
                          <th className="px-3 py-2 text-left">DNS 1</th>
                          <th className="px-3 py-2 text-left">DNS 2</th>
                          <th className="px-3 py-2 text-left">Pool usage</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {mgmtPools.length === 0 ? (
                          <tr><td colSpan={6} className="py-6 text-center text-slate-500">No management IP pool detected from synced ONU assignments.</td></tr>
                        ) : (
                          mgmtPools.map((pool, index) => (
                            <tr key={`pool-${index}`} className="transition-colors hover:bg-slate-50/80 text-slate-700">
                              <td className="px-3 py-2 font-mono font-medium">{dash(pool.ip_address)}</td>
                              <td className="px-3 py-2 font-mono">{dash(pool.subnet_mask)}</td>
                              <td className="px-3 py-2 font-mono">{dash(pool.default_gateway)}</td>
                              <td className="px-3 py-2 font-mono">{dash(pool.dns1)}</td>
                              <td className="px-3 py-2 font-mono">{dash(pool.dns2)}</td>
                              <td className="px-3 py-2 text-slate-600">{dash(pool.usage_label)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-sm font-bold text-slate-900">
                      IPs assigned to ONUs ({mgmtIps?.summary.assigned ?? 0})
                    </h2>
                    <div className="relative min-w-[180px]">
                      <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                      <input
                        value={mgmtSearch}
                        onChange={(event) => setMgmtSearch(event.target.value)}
                        placeholder="Search ONUs…"
                        className="h-8 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-2 text-xs text-slate-900 placeholder-slate-400 shadow-xs focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="max-h-[420px] overflow-x-auto overflow-y-auto rounded-lg border border-slate-200 shadow-xs">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 border-b border-slate-200 bg-slate-50/95 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                        <tr>
                          <th className="px-3 py-2 text-left">ONU</th>
                          <th className="px-3 py-2 text-left">IP address</th>
                          <th className="px-3 py-2 text-left">Subnet mask</th>
                          <th className="px-3 py-2 text-left">Default gateway</th>
                          <th className="px-3 py-2 text-left">DNS 1</th>
                          <th className="px-3 py-2 text-left">DNS 2</th>
                          <th className="px-3 py-2 text-left">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {mgmtAssigned.length === 0 ? (
                          <tr><td colSpan={7} className="py-6 text-center text-slate-500">No management IPs synced for ONUs on this OLT. Run ONU details sync from system config.</td></tr>
                        ) : (
                          mgmtAssigned.map((row) => (
                            <tr key={row.onu_external_id} className="transition-colors hover:bg-slate-50/80 text-slate-700">
                              <td className="px-3 py-2">
                                <Link to={SMARTOLT_PATHS.onuDetail(row.onu_external_id)} className="font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                                  {row.name}
                                </Link>
                              </td>
                              <td className="px-3 py-2 font-mono font-medium">{dash(row.ip_address)}</td>
                              <td className="px-3 py-2 font-mono text-slate-600">{dash(row.subnet_mask)}</td>
                              <td className="px-3 py-2 font-mono text-slate-600">{dash(row.default_gateway)}</td>
                              <td className="px-3 py-2 font-mono text-slate-500">{dash(row.dns1)}</td>
                              <td className="px-3 py-2 font-mono text-slate-500">{dash(row.dns2)}</td>
                              <td className="px-3 py-2">
                                <button
                                  type="button"
                                  onClick={() => openMgmtModal(row)}
                                  className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors"
                                >
                                  Configure
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                {onus.length > 0 ? (
                  <div className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3 shadow-xs">
                    <label className="text-xs">
                      <span className="text-slate-600 font-medium">Configure ONU</span>
                      <select
                        value={mgmtPickId}
                        onChange={(event) => setMgmtPickId(event.target.value)}
                        className="mt-1 block h-9 min-w-[200px] rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none"
                      >
                        <option value="">Select ONU…</option>
                        {onus.map((row) => (
                          <option key={row.onu_external_id} value={row.onu_external_id}>
                            {displayName(row)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      disabled={!mgmtPickId}
                      onClick={() => {
                        const match = onus.find((row) => row.onu_external_id === mgmtPickId)
                        if (match) openMgmtModal(match)
                      }}
                      className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-40"
                    >
                      Configure
                    </button>
                  </div>
                ) : null}
              </div>
            )}

            {tab === "profiles" && (
              <div className="space-y-4">
                <div className="flex flex-wrap justify-end gap-2">
                  <RefreshButton busy={refreshing} onClick={() => refreshSection("config_presets")} label="Refresh presets" />
                  <RefreshButton busy={refreshing} onClick={() => refreshSection("speed_profiles")} label="Refresh speed profiles" />
                </div>
                <p className="text-xs text-slate-500">
                  DBA and line profiles shown in Xelynx are read from the OLT CLI and are not available via SmartOLT REST API.
                </p>
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 shadow-xs">
                  <h2 className="text-sm font-bold text-slate-900">Apply configuration preset</h2>
                  <div className="mt-2 flex flex-wrap items-end gap-2">
                    <label className="text-xs">
                      <span className="text-slate-600 font-medium">ONU</span>
                      <select
                        value={presetOnuId}
                        onChange={(event) => setPresetOnuId(event.target.value)}
                        className="mt-1 block h-9 min-w-[200px] rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none"
                      >
                        <option value="">Select ONU…</option>
                        {onus.map((row) => (
                          <option key={row.onu_external_id} value={row.onu_external_id}>
                            {displayName(row)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs">
                      <span className="text-slate-600 font-medium">Preset</span>
                      <select
                        value={presetKey}
                        onChange={(event) => setPresetKey(event.target.value)}
                        className="mt-1 block h-9 min-w-[200px] rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none"
                      >
                        <option value="">Select preset…</option>
                        {configPresets.map((row) => (
                          <option key={row.id || row.name} value={String(row.id || row.name)}>
                            {row.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      disabled={presetBusy}
                      onClick={applyConfigPreset}
                      className="h-9 rounded-lg bg-blue-600 px-3.5 text-xs font-semibold text-white shadow-xs hover:bg-blue-700 disabled:opacity-50"
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      disabled={presetBusy}
                      onClick={removeConfigPreset}
                      className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <div>
                  <h2 className="mb-2 text-sm font-bold text-slate-900">Configuration presets</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                        <tr>
                          <th className="px-3 py-2 text-left">Name</th>
                          <th className="px-3 py-2 text-left">Board/Port</th>
                          <th className="px-3 py-2 text-left">Reversible</th>
                          <th className="px-3 py-2 text-left">Settings</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {configPresets.length === 0 ? (
                          <tr><td colSpan={4} className="py-6 text-center text-slate-500">No configuration presets returned for this OLT.</td></tr>
                        ) : (
                          configPresets.map((row) => (
                            <tr key={row.id || row.name} className="transition-colors hover:bg-slate-50/80 text-slate-700">
                              <td className="px-3 py-2 font-semibold text-slate-900">{dash(row.name)}</td>
                              <td className="px-3 py-2 text-slate-600">{row.board || row.port ? `${dash(row.board)}/${dash(row.port)}` : "OLT-wide"}</td>
                              <td className={`px-3 py-2 ${row.is_reversible ? "text-emerald-600 font-semibold" : "text-slate-500"}`}>{yesNo(row.is_reversible)}</td>
                              <td className="px-3 py-2 text-slate-600">{dash(presetSettings(row) || row.description)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <h2 className="mb-2 text-sm font-bold text-slate-900">Speed profiles</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                        <tr>
                          <th className="px-3 py-2 text-left">Name</th>
                          <th className="px-3 py-2 text-left">Down / Up</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {speedProfiles.length === 0 ? (
                          <tr><td colSpan={2} className="py-6 text-center text-slate-500">No speed profiles cached.</td></tr>
                        ) : (
                          speedProfiles.map((row: any) => (
                            <tr key={row.profile_id} className="transition-colors hover:bg-slate-50/80 text-slate-700">
                              <td className="px-3 py-2 font-semibold text-slate-900">{dash(row.name)}</td>
                              <td className="px-3 py-2 text-slate-600">{speedLabel(row)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {tab === "cli" && (
              <div className="space-y-3 text-sm">
                <p className="text-xs text-slate-500">
                  CLI user management is not in the SmartOLT public API. Connection details below come from Get OLTs.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-left">Username</th>
                        <th className="px-3 py-2 text-left">User level</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      <tr className="transition-colors hover:bg-slate-50/80 text-slate-700">
                        <td className="px-3 py-2 font-medium">OLT connection</td>
                        <td className="px-3 py-2 font-mono font-medium text-slate-900">{dash(olt?.ip || oltInfo.ip)}</td>
                        <td className="px-3 py-2 text-slate-600">Telnet {dash(olt?.telnet_port || oltInfo.telnet_port)} · SNMP {dash(olt?.snmp_port || oltInfo.snmp_port)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === "vpn" && (
              <div className="space-y-3">
                <RefreshButton busy={refreshing} onClick={() => refreshSection("billing")} label="Refresh VPN / billing" />
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-sm shadow-xs">
                  <p className="text-xs font-medium text-slate-500">Xelynx VPN status</p>
                  <p className="mt-1 text-base font-bold text-emerald-600">
                    {dash(pick(billing, "vpn_status", "tunnel_status", "status") || "See billing details")}
                  </p>
                  {pick(billing, "tunnel_ip", "vpn_ip") ? (
                    <p className="mt-1 text-xs font-mono text-slate-600">Tunnel IP: {dash(pick(billing, "tunnel_ip", "vpn_ip"))}</p>
                  ) : null}
                </div>
                <div className="grid gap-2.5 sm:grid-cols-3 text-sm">
                  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 shadow-xs">
                    <p className="text-xs font-medium text-slate-500">Subscription</p>
                    <p className="mt-1 font-bold text-slate-900">{dash(pick(billing, "subscription_status", "status", "subscription"))}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 shadow-xs">
                    <p className="text-xs font-medium text-slate-500">End date</p>
                    <p className="mt-1 font-bold text-slate-900">{dash(pick(billing, "subscription_end_date", "end_date", "expires"))}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 shadow-xs">
                    <p className="text-xs font-medium text-slate-500">OLT</p>
                    <p className="mt-1 font-bold text-slate-900">{dash(pick(billing, "name", "olt_name") || name)}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-500">VPN tunnel controls (re-add routes, RouterOS config) are Xelynx web UI only. Billing details come from SmartOLT Get billing details.</p>
              </div>
            )}

            {tab === "graphs" && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={graphOnuId}
                    onChange={(event) => {
                      const next = event.target.value
                      setGraphOnuId(next)
                      if (next) loadGraph(next, graphKind)
                    }}
                    className="h-9 min-w-[220px] rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">Select ONU</option>
                    {onus.slice(0, 80).map((row) => (
                      <option key={row.onu_external_id} value={row.onu_external_id}>
                        {displayName(row)}
                      </option>
                    ))}
                  </select>
                  <select
                    value={graphKind}
                    onChange={(event) => {
                      const next = event.target.value as "traffic" | "signal"
                      setGraphKind(next)
                      if (graphOnuId) loadGraph(graphOnuId, next)
                    }}
                    className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-900 shadow-xs focus:border-blue-500 focus:outline-none"
                  >
                    <option value="traffic">Traffic</option>
                    <option value="signal">Signal</option>
                  </select>
                  <Link to={SMARTOLT_PATHS.xrtg} className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">
                    Open XRTG
                  </Link>
                </div>
                {graphLoading ? <p className="text-sm text-slate-500">Loading graph…</p> : null}
                {graphUrl ? <img src={graphUrl} alt="OLT ONU graph" className="max-h-[420px] w-full rounded-xl border border-slate-200 object-contain bg-white shadow-xs" /> : (
                  <p className="text-sm text-slate-500">SmartOLT does not expose an OLT-wide graph endpoint. Choose an ONU to load its traffic or signal graph.</p>
                )}
              </div>
            )}

            {tab === "voip" && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left">ONU</th>
                      <th className="px-3 py-2 text-left">VoIP</th>
                      <th className="px-3 py-2 text-left">M-VLAN</th>
                      <th className="px-3 py-2 text-left">Mgmt IP</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {voipOnus.length === 0 ? (
                      <tr><td colSpan={4} className="py-6 text-center text-slate-500">No VoIP-enabled ONUs cached for this OLT. VoIP profile CRUD is not in the SmartOLT public API.</td></tr>
                    ) : (
                      voipOnus.map((row) => (
                        <tr key={row.onu_external_id} className="transition-colors hover:bg-slate-50/80 text-slate-700">
                          <td className="px-3 py-2">
                            <Link to={SMARTOLT_PATHS.onuDetail(row.onu_external_id)} className="font-semibold text-blue-600 hover:text-blue-800 hover:underline">{displayName(row)}</Link>
                          </td>
                          <td className="px-3 py-2">{dash(row.voip)}</td>
                          <td className="px-3 py-2 font-mono text-slate-600">{dash(row.mgmt_vlan)}</td>
                          <td className="px-3 py-2 font-mono text-slate-600">{dash(row.mgmt_ip)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "alarms" && (
              <div className="space-y-3">
                <div className="flex justify-end">
                  <Link to={`${SMARTOLT_PATHS.alarms}?q=${encodeURIComponent(name)}`} className="inline-flex h-8 items-center rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">
                    All alarms
                  </Link>
                </div>
                <table className="w-full text-xs">
                  <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Time</th>
                      <th className="px-3 py-2 text-left">Severity</th>
                      <th className="px-3 py-2 text-left">Device</th>
                      <th className="px-3 py-2 text-left">Message</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {alarms.length === 0 ? (
                      <tr><td colSpan={4} className="py-6 text-center text-slate-500">No open alarms for this OLT.</td></tr>
                    ) : (
                      alarms.slice(0, 40).map((row) => (
                        <tr key={row.id} className="transition-colors hover:bg-slate-50/80 text-slate-700">
                          <td className="px-3 py-2 text-slate-500 font-mono">{dateTime(row.time)}</td>
                          <td className="px-3 py-2">{dash(row.severity)}</td>
                          <td className="px-3 py-2 font-semibold text-slate-900">{dash(row.device || row.onu_name)}</td>
                          <td className="px-3 py-2 text-slate-600">{dash(row.message)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "templates" && (
              <div className="space-y-3">
                <RefreshButton busy={refreshing} onClick={() => refreshSection("presets")} label="Refresh Templates" />
                <p className="text-xs text-slate-500">Authorization presets are the SmartOLT API equivalent of provisioning templates. Use them from Unconfigured to authorize ONUs.</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">ONU type</th>
                        <th className="px-3 py-2 text-left">Mode</th>
                        <th className="px-3 py-2 text-left">WAN VLAN</th>
                        <th className="px-3 py-2 text-left">TR-069</th>
                        <th className="px-3 py-2 text-left">Down / Up</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {authPresets.length === 0 ? (
                        <tr><td colSpan={6} className="py-6 text-center text-slate-500">No authorization presets returned for this OLT.</td></tr>
                      ) : (
                        authPresets.map((row) => (
                          <tr key={row.id || row.name} className="transition-colors hover:bg-slate-50/80 text-slate-700">
                            <td className="px-3 py-2 font-semibold text-slate-900">{dash(row.name)}</td>
                            <td className="px-3 py-2 text-slate-600">{dash(row.applies?.onu_type_name || row.applies?.fallback_onu_type_name || row.criteria?.onu_type_name)}</td>
                            <td className="px-3 py-2 text-slate-600">{dash(row.applies?.mode)}</td>
                            <td className="px-3 py-2 font-mono text-slate-600">{dash(row.applies?.vlan)}</td>
                            <td className={`px-3 py-2 ${row.applies?.sets_tr069 ? "text-emerald-600 font-semibold" : "text-slate-500"}`}>{yesNo(row.applies?.sets_tr069)}</td>
                            <td className="px-3 py-2 text-slate-600">{[row.applies?.download_speed_name, row.applies?.upload_speed_name].filter(Boolean).join(" / ") || "-"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </section>

        {tab === "overview" && !loading && onus.length > 0 && (
          <section className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-xs">
            <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3 text-sm font-bold text-slate-900">ONUs on this OLT</div>
            <table className="w-full text-xs">
              <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Signal</th>
                  <th className="px-3 py-2 text-left">PON</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {onus.slice(0, 12).map((row) => (
                  <tr key={row.onu_external_id} className="transition-colors hover:bg-slate-50/80 text-slate-700">
                    <td className="px-3 py-2">
                      <Link to={SMARTOLT_PATHS.onuDetail(row.onu_external_id)} className="font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                        {displayName(row)}
                      </Link>
                    </td>
                    <td className="px-3 py-2 font-medium">{statusKind(row)}</td>
                    <td className="px-3 py-2">{formatSignal(row.signal_rx)}</td>
                    <td className="px-3 py-2 text-slate-600">{dash(row.pon_port || `${row.board || ""}/${row.port || ""}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

      <SmartOLTMgmtIpModal
        open={Boolean(mgmtModalOnu)}
        onu={mgmtModalOnu}
        poolDefaults={mgmtPools[0]}
        onClose={() => setMgmtModalOnu(null)}
        onSuccess={reloadMgmtIps}
      />
    </div>
  )
}
